import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { all, task } from 'ember-concurrency';
import PowerSelectMultiple from 'ember-power-select/components/power-select-multiple';
import type LocationRole from 'garaje/models/location-role';
import type User from 'garaje/models/user';
import _uniqBy from 'lodash/uniqBy';
import { localCopy } from 'tracked-toolbox';

interface SecurityIdScanningEmailAlertsSignature {
  Args: {
    idScanContacts: User[];
    onUpdateContacts: (users: User[]) => void;
    searchUsers: (options: { term: string }) => Promise<LocationRole[]>;
  };
  Element: HTMLFieldSetElement;
}

export default class SecurityIdScanningEmailAlerts extends Component<SecurityIdScanningEmailAlertsSignature> {
  @tracked hasChanges = false;

  @localCopy('args.idScanContacts', []) newIdScanContacts!: User[];

  searchUsersTask = task(async (term: string) => {
    const re = new RegExp(`.*${term}.*`, 'i');
    const userRoles = await this.args.searchUsers({ term });
    let users = await all(userRoles.map((role) => role.user));
    const currentContactsIds = this.newIdScanContacts.map((contact) => contact.id);

    // deduplicate since roles could point to same user
    users = _uniqBy(users, 'id')
      // remove users that are already selected
      .filter((user) => currentContactsIds.indexOf(user.id) === -1)
      .filter((user) => user.fullName.match(re));

    return users;
  });

  addIdScanContacts = (users: User[]): void => {
    this.hasChanges = true;
    this.newIdScanContacts = users;
    this.args.onUpdateContacts(users);
  };

  <template>
    <fieldset ...attributes>
      <div class="mt-8 mr-16">
        <h4>Alerts</h4>
        <p class="mb-4">
          Choose Global, Location, or Front Desk Admins to alert via email when a visitor’s ID is rejected.
        </p>
        <div class="flex flex-auto">
          <div class="w-4/6 grow" data-test-idscan-contacts>
            <PowerSelectMultiple
              @searchEnabled={{true}}
              @search={{this.searchUsersTask.perform}}
              @placeholder="Search administrators"
              @allowClear={{true}}
              @selected={{this.newIdScanContacts}}
              @onChange={{this.addIdScanContacts}}
              as |idScanContact|
            >
              {{idScanContact.fullName}}
            </PowerSelectMultiple>
          </div>
        </div>
      </div>
    </fieldset>
  </template>
}
